import { Component, computed, inject, input, linkedSignal, output, signal } from '@angular/core';
import { AssetFilters } from '../types';
import { FormsModule } from '@angular/forms';
import {
  AssetType,
  AccessLevel,
  FilterUpdate,
  AssetList,
} from '../../services/asset-service/types';
import { AssetTypeOption, SidebarGroup } from './types';
import { objectEntries } from '../../shared/utils';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { tablerDatabase, tablerSparkle, tablerTool, tablerWorld } from '@ng-icons/tabler-icons';
import { AssetService } from '../../services/asset-service/asset.service';
import { HttpResourceRef } from '@angular/common/http';

@Component({
  selector: 'asset-catalog-filters',
  templateUrl: './asset-catalog-filters.html',
  styleUrl: './asset-catalog-filters.scss',
  imports: [FormsModule, NgIcon],
  providers: [provideIcons({ tablerDatabase, tablerSparkle, tablerTool, tablerWorld })],
})
export class AssetCatalogFilters {
  filters = input.required<AssetFilters>();
  filterChange = output<(prev: AssetFilters) => AssetFilters>();
  clearFilters = input.required<() => void>();

  assetService = inject(AssetService);

  sidebarGroupsExpanded = signal<Record<SidebarGroup, boolean>>({
    trust: true,
    access: true,
    sector: false,
    themes: false,
    regions: false,
  });
  toggleSidebarGroup = (group: SidebarGroup) => {
    this.sidebarGroupsExpanded.update((prev) => {
      return {
        ...prev,
        [group]: !prev[group],
      };
    });
  };

  readonly assetListData = this.assetService.getAssetCatalogList(this.filters);
  readonly assetFilterCounts = linkedSignal<
    HttpResourceRef<AssetList | undefined>,
    FilterUpdate | undefined
  >({
    source: () => this.assetListData,
    computation: (newData, prev) => {
      if (newData.hasValue()) {
        return newData.value().filter_update;
      }
      // show previous data while loading so that the filters don't disappear
      // when the request is still pending
      return prev?.value;
    },
  });

  readonly assetTypeOptionData: Record<AssetType, AssetTypeOption> = {
    Dataset: {
      icon: 'tablerDatabase',
    },
    'AI Agent': {
      icon: 'tablerSparkle',
    },
    'AI Tool': {
      icon: 'tablerTool',
    },
    'AI Product': {
      icon: 'tablerWorld',
    },
  };
  readonly assetTypeOptions = computed(() => {
    return objectEntries(this.assetTypeOptionData).map(([value, { icon }]) => {
      return {
        value,
        icon,
        label: value,
      };
    });
  });

  readonly trustStatusOptions = computed(() => {
    const counts = this.assetFilterCounts();
    if (!counts) return [];
    return counts.trust_status.map(({ key, count }) => {
      return {
        key,
        count,
        selected: this.filters().trust_status.includes(key),
      };
    });
  });
  onToggleTrustStatusOption = (value: string) => {
    this.filterChange.emit((prev) => {
      const isActive = prev.trust_status.includes(value);
      const oldValues = prev.trust_status;
      const newValues = isActive ? oldValues.filter((v) => v !== value) : [...oldValues, value];
      const updated: AssetFilters = {
        ...prev,
        trust_status: newValues,
      };
      return updated;
    });
  };

  readonly accessOptions = computed(() => {
    const counts = this.assetFilterCounts();
    if (!counts) return [];
    return counts.access.map(({ key, count }) => {
      return {
        key,
        count,
        selected: this.filters().access.includes(key),
      };
    });
  });
  onToggleAccessOption = (value: AccessLevel) => {
    this.filterChange.emit((prev) => {
      const isActive = prev.access.includes(value);
      const oldValues = prev.access;
      const newValues = isActive ? oldValues.filter((v) => v !== value) : [...oldValues, value];
      const updated: AssetFilters = {
        ...prev,
        access: newValues,
      };
      return updated;
    });
  };

  readonly sectorOptions = computed(() => {
    const counts = this.assetFilterCounts();
    if (!counts) return [];
    return counts.sector.map(({ key, count }) => {
      return {
        key,
        count,
        selected: this.filters().sector.includes(key),
      };
    });
  });
  onToggleSectorOption = (value: string) => {
    this.filterChange.emit((prev) => {
      const isActive = prev.sector.includes(value);
      const oldValues = prev.sector;
      const newValues = isActive ? oldValues.filter((v) => v !== value) : [...oldValues, value];
      const updated: AssetFilters = {
        ...prev,
        sector: newValues,
      };
      return updated;
    });
  };

  readonly themesOptions = computed(() => {
    const counts = this.assetFilterCounts();
    if (!counts) return [];
    return counts.themes.map(({ key, count }) => {
      return {
        key,
        count,
        selected: this.filters().themes.includes(key),
      };
    });
  });
  onToggleThemesOption = (value: string) => {
    this.filterChange.emit((prev) => {
      const isActive = prev.themes.includes(value);
      const oldValues = prev.themes;
      const newValues = isActive ? oldValues.filter((v) => v !== value) : [...oldValues, value];
      const updated: AssetFilters = {
        ...prev,
        themes: newValues,
      };
      return updated;
    });
  };

  readonly regionOptions = computed(() => {
    const counts = this.assetFilterCounts();
    if (!counts) return [];
    return counts.regions.map(({ key, count }) => {
      return {
        key,
        count,
        selected: this.filters().regions.includes(key),
      };
    });
  });
  onToggleRegionOption = (value: string) => {
    this.filterChange.emit((prev) => {
      const isActive = prev.regions.includes(value);
      const oldValues = prev.regions;
      const newValues = isActive ? oldValues.filter((v) => v !== value) : [...oldValues, value];
      const updated: AssetFilters = {
        ...prev,
        regions: newValues,
      };
      return updated;
    });
  };

  get activeFilterCount(): number {
    const { asset_type, trust_status, access, sector, themes, regions } = this.filters();
    return (
      asset_type.length +
      trust_status.length +
      access.length +
      sector.length +
      themes.length +
      regions.length
    );
  }

  clearAssetTypes(): void {
    this.filterChange.emit((prev) => {
      const updated: AssetFilters = {
        ...prev,
        asset_type: [],
      };
      return updated;
    });
  }

  toggleAssetType(value: AssetType): void {
    this.filterChange.emit((prev) => {
      const isActive = prev.asset_type.includes(value);
      const oldValues = prev.asset_type;
      const newValues = isActive ? oldValues.filter((v) => v !== value) : [...oldValues, value];
      const updated: AssetFilters = {
        ...prev,
        asset_type: newValues,
      };
      return updated;
    });
  }

  clearSidebarGroup = (group: SidebarGroup) => {
    switch (group) {
      case 'trust': {
        this.filterChange.emit((prev) => {
          const updated: AssetFilters = {
            ...prev,
            trust_status: [],
          };
          return updated;
        });
        break;
      }
      case 'access': {
        this.filterChange.emit((prev) => {
          const updated: AssetFilters = {
            ...prev,
            access: [],
          };
          return updated;
        });
        break;
      }
      case 'sector': {
        this.filterChange.emit((prev) => {
          const updated: AssetFilters = {
            ...prev,
            sector: [],
          };
          return updated;
        });
        break;
      }
      case 'themes': {
        this.filterChange.emit((prev) => {
          const updated: AssetFilters = {
            ...prev,
            themes: [],
          };
          return updated;
        });
        break;
      }
      case 'regions': {
        this.filterChange.emit((prev) => {
          const updated: AssetFilters = {
            ...prev,
            regions: [],
          };
          return updated;
        });
        break;
      }
    }
  };
}
